import { Fragment } from 'react'
import { StaticImageData } from 'next/legacy/image'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors, fontWeights } from '@/styles'
import useLanguage from '@/hooks/use-language'
import useMediaQuery from '@/hooks/use-media-query'
import Avatar from '@/components/avatar'

const Wrapper = styled.div<{ height: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 920px;
  height: ${props => props.height}px;
  border-radius: 10px;
  background-color: ${colors.secondary};
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.07);
  padding: 80px 40px;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: 85vw;
    height: 90%;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 60px 40px;
    width: 325px;
    height: 100%;
  }
`

const AvatarWrapper = styled.div`
  padding-bottom: 30px;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    padding-bottom: 25px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding-bottom: 20px;
  }
`

const Quote = styled.p<{ isLongText: boolean }>`
  color: ${colors.white};
  font-size: ${props => (props.isLongText ? 20 : 24)}px;
  font-weight: ${fontWeights.medium};
  font-family: var(--font-open-sans, 'Open Sans', sans-serif);
  font-style: italic;
  line-height: 33px;
  text-align: center;
  width: 650px;
  margin-bottom: 60px;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: 80%;
    font-size: 19px;
    line-height: 26px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: ${props => (props.isLongText ? 12 : 14)}px;
    letter-spacing: -0.4px;
    line-height: 19px;
    width: 250px;
  }
`

const Name = styled.p`
  color: ${colors.white};
  font-weight: bold;
  font-size: 18px;
  text-align: center;
  letter-spacing: 0px;
  font-family: var(--font-open-sans, 'Open Sans', sans-serif);
  margin-bottom: 0;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 14px;
    line-height: 19px;
  }
`

const InfoWrapper = styled.div<{ hasVideo: boolean }>`
  display: flex;
  flex-direction: row;
  > div:not(:last-of-type) {
    position: relative;
    margin-right: 10px;
    padding-right: 10px;
    &:after {
      position: absolute;
      content: '';
      height: 20px;
      width: 2px;
      background: ${colors.mediumGrey};
    }
  }
`

const Info = styled.p`
  color: ${colors.white};
  font-family: var(--font-open-sans, 'Open Sans', sans-serif);
  font-size: 18px;
  font-weight: ${fontWeights.medium};
  letter-spacing: 0;
  line-height: 24px;
  text-align: center;
  margin: 0;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 14px;
    letter-spacing: -0.4px;
    line-height: 19px;
  }
`

const Link = styled.a`
  text-decoration: none;
  color: ${colors.link};
  font-weight: ${fontWeights.medium};
  font-size: 18px;
  padding-left: 5px;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 14px;
  }
`

interface Props {
  children: React.ReactNode
  name: string
  title: string
  avatar?: string | StaticImageData
  link?: string
  height?: number
}

const Testimonial: React.FC<Props> = ({
  children,
  name,
  title,
  avatar,
  link,
  height = 510
}) => {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const isFrench = language.value === 'fr'
  const isLongText = isFrench && (name === 'Rob' || name === 'Klaus')
  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const avatarDimension = displayTablet ? 70 : 110

  return (
    <Wrapper height={height}>
      {avatar && (
        <AvatarWrapper>
          <Avatar
            width={avatarDimension}
            height={avatarDimension}
            location={avatar}
          />
        </AvatarWrapper>
      )}
      <Quote isLongText={isLongText}>{children}</Quote>
      <Name>{name}</Name>
      <InfoWrapper hasVideo={!!link}>
        <Info>{title}</Info>
        {!!link && (
          <Fragment>
            <Info style={{ paddingLeft: '5px' }}>|</Info>
            <Link target="_blank" rel="noopener noreferrer" href={link}>
              {t('common.watch_video', 'Watch Video')}
            </Link>
          </Fragment>
        )}
      </InfoWrapper>
    </Wrapper>
  )
}

export default Testimonial
